
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, DollarSign, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useWaveClientData } from '@/hooks/useWaveClientData';
import WaveDocumentViewer from './WaveDocumentViewer';
import WaveAccountOverview from './WaveAccountOverview';
import WaveSyncStatus from './WaveSyncStatus';

interface WaveClientDashboardProps {
  clientId: string;
  waveCustomerId?: string;
}

const WaveClientDashboard: React.FC<WaveClientDashboardProps> = ({ 
  clientId, 
  waveCustomerId 
}) => {
  const { 
    estimates, 
    invoices, 
    accountSummary, 
    isLoading, 
    error,
    refreshData 
  } = useWaveClientData(clientId, waveCustomerId);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-3">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <p>Unable to load your billing data. Please try again later.</p>
          </div>
          <Button 
            variant="outline" 
            onClick={refreshData}
            className="mt-4"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Account Overview */}
      <WaveAccountOverview 
        accountSummary={accountSummary}
        waveCustomerId={waveCustomerId}
      />

      {/* Quick Stats and Sync Status */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              Active Estimates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {estimates.filter(e => e.status === 'pending').length}
            </div>
            <p className="text-xs text-gray-500">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Outstanding Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {invoices.filter(i => i.status === 'unpaid').length}
            </div>
            <p className="text-xs text-gray-500">
              ${invoices
                .filter(i => i.status === 'unpaid')
                .reduce((sum, i) => sum + i.total, 0)
                .toFixed(2)} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-600" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {Math.max(estimates.length, invoices.length)}
            </div>
            <p className="text-xs text-gray-500">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-purple-600" />
              Completed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {invoices.filter(i => i.status === 'paid').length}
            </div>
            <p className="text-xs text-gray-500">Paid invoices</p>
          </CardContent>
        </Card>

        {/* Sync Status */}
        <WaveSyncStatus 
          clientId={clientId}
          waveCustomerId={waveCustomerId}
        />
      </div>

      {/* Recent Documents */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Estimates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Recent Estimates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <WaveDocumentViewer 
              documents={estimates.slice(0, 5)}
              type="estimate"
            />
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Recent Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <WaveDocumentViewer 
              documents={invoices.slice(0, 5)}
              type="invoice"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WaveClientDashboard;
